import { chapterApi } from '@/api/chapterApi'
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { friendlyMessage } from '@/api/http'
import { jobApi } from '@/api/jobApi'
import { Button } from '@/components/ui/button'
import { NumberInput } from '@/components/ui/number-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { JOB_CHAPTER_MODES, type JobChapterMode } from '@/constants/chapterModes'
import type { BatchJobInput, JobView, Project, SubmitJobInput } from '@/types/nf'
import { formatNumber } from '@/utils/format'

export { JOB_CHAPTER_MODES }
export type { JobChapterMode }

export interface SubmitJobFormValues {
  chapter_no: number
  outline: string
  target_words: number
  chapter_mode: JobChapterMode
  volume_no?: number
}

export interface BatchSubmitJobFormValues {
  start_chapter_no: number
  chapter_count: number
  outline: string
  target_words: number
  chapter_mode: JobChapterMode
  volume_no?: number
}

type SubmitMode = 'single' | 'batch'
type SubmitJobDrawerFormValues = SubmitJobFormValues & BatchSubmitJobFormValues
type SubmitJobDrawerValues = Omit<
  SubmitJobDrawerFormValues,
  'chapter_no' | 'start_chapter_no' | 'chapter_count' | 'target_words' | 'volume_no'
> & {
  chapter_no?: number
  start_chapter_no?: number
  chapter_count?: number
  target_words?: number
  volume_no?: number
}
type FieldKey = keyof SubmitJobDrawerValues

const TARGET_WORDS_MIN = 1500
const TARGET_WORDS_MAX = 8000
const DEFAULT_TARGET_WORDS = 5000
const BATCH_CHAPTER_COUNT_MIN = 1
const BATCH_CHAPTER_COUNT_MAX = 50

export function defaultSubmitJobValues(jobs: JobView[]): SubmitJobFormValues {
  const maxChapterNo = jobs.reduce((max, job) => Math.max(max, job.chapter_no), 0)
  return {
    chapter_no: maxChapterNo + 1 || 1,
    outline: '',
    target_words: DEFAULT_TARGET_WORDS,
    chapter_mode: 'regular',
    volume_no: 1,
  }
}

function normalizeLocale(language: string) {
  return language.trim().toLowerCase().split(/[-_]/)[0] || 'zh'
}

function assertPositiveInteger(name: string, value: number) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new Error(`${name} must be a positive integer`)
  }
}

function assertTargetWords(value: number) {
  if (!Number.isInteger(value) || value < TARGET_WORDS_MIN || value > TARGET_WORDS_MAX) {
    throw new Error(`target_words must be between ${TARGET_WORDS_MIN} and ${TARGET_WORDS_MAX}`)
  }
}

function assertChapterMode(value: JobChapterMode) {
  if (!JOB_CHAPTER_MODES.includes(value)) {
    throw new Error('chapter_mode is invalid')
  }
}

function normalizeOutline(outline: string) {
  const trimmed = outline.trim()
  if (!trimmed) {
    throw new Error('outline is required')
  }
  return trimmed
}

export function buildSubmitJobInput(projectId: string, language: string, values: SubmitJobFormValues): SubmitJobInput {
  assertPositiveInteger('chapter_no', values.chapter_no)
  if (values.volume_no != null) assertPositiveInteger('volume_no', values.volume_no)

  assertTargetWords(values.target_words)
  assertChapterMode(values.chapter_mode)

  const outline = normalizeOutline(values.outline)
  const input: SubmitJobInput = {
    project_id: projectId,
    chapter_no: values.chapter_no,
    outline,
    target_words: values.target_words,
    lang: normalizeLocale(language),
    chapter_mode: values.chapter_mode,
  }
  if (values.volume_no != null) input.volume_no = values.volume_no
  return input
}

export function buildBatchJobInput(projectId: string, language: string, values: BatchSubmitJobFormValues): BatchJobInput {
  assertPositiveInteger('start_chapter_no', values.start_chapter_no)
  if (values.volume_no != null) assertPositiveInteger('volume_no', values.volume_no)
  if (
    !Number.isInteger(values.chapter_count) ||
    values.chapter_count < BATCH_CHAPTER_COUNT_MIN ||
    values.chapter_count > BATCH_CHAPTER_COUNT_MAX
  ) {
    throw new Error(`chapter_count must be between ${BATCH_CHAPTER_COUNT_MIN} and ${BATCH_CHAPTER_COUNT_MAX}`)
  }
  assertTargetWords(values.target_words)
  assertChapterMode(values.chapter_mode)

  const outline = normalizeOutline(values.outline)
  const lang = normalizeLocale(language)
  return {
    project_id: projectId,
    items: Array.from({ length: values.chapter_count }, (_, index) => {
      const item: BatchJobInput['items'][number] = {
        chapter_no: values.start_chapter_no + index,
        outline,
        target_words: values.target_words,
        lang,
        chapter_mode: values.chapter_mode,
      }
      if (values.volume_no != null) item.volume_no = values.volume_no
      return item
    }),
  }
}

function getErrorMessage(err: unknown, fallback: string) {
  return friendlyMessage(err, fallback)
}

function createDrawerValues(defaults: SubmitJobFormValues): SubmitJobDrawerValues {
  return {
    ...defaults,
    start_chapter_no: defaults.chapter_no,
    chapter_count: 1,
  }
}

function Field({
  children,
  error,
  hint,
  label,
}: {
  children: ReactNode
  error?: string
  hint?: string
  label: string
}) {
  return (
    <div className="flex flex-col gap-2 text-sm font-medium text-foreground">
      <span className="block">{label}</span>
      {children}
      {hint ? <span className="text-xs font-normal text-muted-foreground">{hint}</span> : null}
      {error ? <span className="text-xs font-normal text-destructive">{error}</span> : null}
    </div>
  )
}

export function SubmitJobDrawer({
  open,
  project,
  branchId = 'main',
  onClose,
  onSubmitted,
}: {
  open: boolean
  project: Project | null
  jobs: JobView[]
  branchId?: string
  onClose: () => void
  onSubmitted: (projectId: string) => Promise<void>
}) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<SubmitMode>('single')
  const [defaultsLoading, setDefaultsLoading] = useState(false)
  const [defaultsReady, setDefaultsReady] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [values, setValues] = useState<SubmitJobDrawerValues>({ outline: '', chapter_mode: 'regular', chapter_count: 1 })
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({})
  const wordsHint = t('submitJob.wordsHint', {
    min: formatNumber(TARGET_WORDS_MIN),
    max: formatNumber(TARGET_WORDS_MAX),
    defaultValue: values.target_words ? formatNumber(values.target_words) : '—',
  })

  useEffect(() => {
    if (!open || !project) return
    let cancelled = false
    setDefaultsLoading(true)
    setDefaultsReady(false)
    setMode('single')
    setError(null)
    setFieldErrors({})
    void chapterApi.nextDefaults(project.id, branchId).then((next) => {
      if (cancelled) return
      setValues(createDrawerValues(next))
      setDefaultsReady(true)
    }).catch((error) => {
      if (!cancelled) setError(friendlyMessage(error, t('chapter.generate.defaultsFailed')))
    }).finally(() => { if (!cancelled) setDefaultsLoading(false) })
    return () => { cancelled = true }
  }, [open, project?.id, branchId, t])

  const closeAndReset = () => {
    setValues({ outline: '', chapter_mode: 'regular', chapter_count: 1 })
    setError(null)
    setFieldErrors({})
    onClose()
  }

  const updateValue = <K extends FieldKey>(key: K, value: SubmitJobDrawerValues[K]) => {
    setValues((current) => ({ ...current, [key]: value }))
    setFieldErrors((current) => ({ ...current, [key]: undefined }))
  }

  const validateFields = () => {
    const nextErrors: Partial<Record<FieldKey, string>> = {}

    if (mode === 'batch') {
      if (!values.start_chapter_no) nextErrors.start_chapter_no = t('submitJob.startChapterNoRequired')
      if (!values.chapter_count) {
        nextErrors.chapter_count = t('submitJob.chapterCountRequired')
      } else if (values.chapter_count < BATCH_CHAPTER_COUNT_MIN || values.chapter_count > BATCH_CHAPTER_COUNT_MAX) {
        nextErrors.chapter_count = t('submitJob.chapterCountHint', { max: BATCH_CHAPTER_COUNT_MAX })
      }
    } else if (!values.chapter_no) {
      nextErrors.chapter_no = t('submitJob.chapterNoRequired')
    }

    if (!values.outline.trim()) nextErrors.outline = t('submitJob.outlineRequired')
    if (!values.target_words) {
      nextErrors.target_words = t('submitJob.targetWordsRequired')
    } else if (values.target_words < TARGET_WORDS_MIN || values.target_words > TARGET_WORDS_MAX) {
      nextErrors.target_words = wordsHint
    }
    if (!values.chapter_mode) nextErrors.chapter_mode = t('submitJob.chapterModeRequired')

    setFieldErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const submit = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault()
    if (!project || !defaultsReady) return
    setError(null)
    if (!validateFields()) return
    const submitValues = values as SubmitJobDrawerFormValues
    setSubmitting(true)
    try {
      const language = project.content_language || 'zh'
      if (mode === 'batch') {
        const batch = buildBatchJobInput(project.id, language, submitValues)
        const response = await jobApi.batch({ ...batch, items: batch.items.map((item) => ({ ...item, branch_id: branchId })) })
        toast.success(t('submitJob.batchSubmitted', { count: response.submitted }))
      } else {
        await jobApi.submit({ ...buildSubmitJobInput(project.id, language, submitValues), branch_id: branchId })
        toast.success(t('submitJob.submitted'))
      }
      await onSubmitted(project.id)
      closeAndReset()
    } catch (err) {
      const nextError = getErrorMessage(err, t('common.serviceError'))
      setError(nextError)
      toast.error(nextError)
    } finally {
      setSubmitting(false)
    }
  }

  const disabled = submitting || !project || defaultsLoading || !defaultsReady

  return (
    <Sheet open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) closeAndReset()
    }}>
      <SheetContent className="w-full gap-0 p-0 sm:max-w-[520px]">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>{t('submitJob.title')}</SheetTitle>
          <SheetDescription>{t('submitJob.description')}</SheetDescription>
        </SheetHeader>

        <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
            {error ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            ) : null}

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-foreground">{t('submitJob.mode')}</span>
              <ToggleGroup
                className="grid w-full grid-cols-2"
                disabled={disabled}
                type="single"
                value={mode}
                onValueChange={(value) => {
                  if (value) setMode(value as SubmitMode)
                }}
              >
                <ToggleGroupItem value="single">{t('submitJob.singleMode')}</ToggleGroupItem>
                <ToggleGroupItem value="batch">{t('submitJob.batchMode')}</ToggleGroupItem>
              </ToggleGroup>
            </div>

            {mode === 'batch' ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t('submitJob.startChapterNo')} error={fieldErrors.start_chapter_no}>
                  <NumberInput
                    disabled={disabled}
                    min={1}
                    value={values.start_chapter_no}
                    onChange={(value) => updateValue('start_chapter_no', value)}
                  />
                </Field>

                <Field
                  label={t('submitJob.chapterCount')}
                  error={fieldErrors.chapter_count}
                  hint={t('submitJob.chapterCountHint', { max: BATCH_CHAPTER_COUNT_MAX })}
                >
                  <NumberInput
                    disabled={disabled}
                    min={BATCH_CHAPTER_COUNT_MIN}
                    max={BATCH_CHAPTER_COUNT_MAX}
                    value={values.chapter_count}
                    onChange={(value) => updateValue('chapter_count', value)}
                  />
                </Field>
              </div>
            ) : (
              <Field label={t('submitJob.chapterNo')} error={fieldErrors.chapter_no}>
                <NumberInput
                  disabled={disabled}
                  min={1}
                  value={values.chapter_no}
                  onChange={(value) => updateValue('chapter_no', value)}
                />
              </Field>
            )}

            <Field
              label={mode === 'batch' ? t('submitJob.sharedOutline') : t('submitJob.outline')}
              error={fieldErrors.outline}
              hint={`${values.outline.length}/4000`}
            >
              <textarea
                className="min-h-[144px] w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={disabled}
                maxLength={4000}
                value={values.outline}
                onChange={(event) => updateValue('outline', event.target.value)}
              />
            </Field>

            <Field label={t('submitJob.targetWords')} error={fieldErrors.target_words} hint={wordsHint}>
              <NumberInput
                disabled={disabled}
                min={TARGET_WORDS_MIN}
                max={TARGET_WORDS_MAX}
                value={values.target_words}
                onChange={(value) => updateValue('target_words', value)}
              />
            </Field>

            <Field label={t('submitJob.chapterMode')} error={fieldErrors.chapter_mode}>
              <Select
                disabled={disabled}
                value={values.chapter_mode}
                onValueChange={(value) => updateValue('chapter_mode', value as JobChapterMode)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {JOB_CHAPTER_MODES.map((chapterMode) => (
                    <SelectItem key={chapterMode} value={chapterMode}>
                      {t(`jobChapterMode.${chapterMode}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label={t('submitJob.volumeNo')} hint={t('submitJob.volumeNoHint')}>
              <NumberInput
                disabled={disabled}
                min={1}
                value={values.volume_no}
                onChange={(value) => updateValue('volume_no', value)}
              />
            </Field>

            <div className="flex flex-col gap-0.5 text-sm">
              <span className="text-muted-foreground">{t('submitJob.lang')}</span>
              <span className="nf-mono text-foreground">{normalizeLocale(project?.content_language || 'zh')}</span>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={closeAndReset} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={disabled}>
              {t('submitJob.submit')}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
